
/**
 * Module dependencies.
 */

import { ChangeEvent, ReactNode, useCallback } from 'react';
import { FieldAlert } from '../../forms/field-alert';
import { FieldError } from '../../forms/field-error';
import { FormGroup } from '../../forms/fields-styled-components';
import { color } from 'react-components/styles';
import { theme } from 'styled-tools';
import { useField, useFormState } from '@seegno/react-forms';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  'aria-describedby'?: string;
  className?: string;
  disabled?: boolean;
  helpText?: ReactNode;
  id?: string;
  label: string;
  max?: number;
  min?: number;
  name: string;
  step?: number;
};

/**
 * Get progress.
 */

function getProgress(value: number, max: number, min: number): string {
  return `${(value - min) / (max - min) * 100}%`;
}

/**
 * Thumb styles.
 */

const thumbStyles = css`
  appearance: none;
  background-color: var(--progressBg);
  border: none;
  border-radius: calc(var(--thumbSize) / 2);
  cursor: pointer;
  height: var(--thumbSize);
  margin-top: calc(((var(--thumbSize) - var(--trackSize)) / 2) * -1);
  width: var(--thumbSize);
`;

/**
 * Progress styles.
 */

const progressStyles = css<{ max: number; min: number; value: number }>`
  background: linear-gradient(
    90deg, 
    var(--progressBg) ${props => getProgress(props.value, props.max, props.min)}, 
    var(--trackBg) ${props => getProgress(props.value, props.max, props.min)}  
  );
  border-radius: ${theme('dimensions.borderRadius')}px;
  height: var(--trackSize);
`;

/**
 * `StyledFormGroup` styled component.
 */

const StyledFormGroup = styled(FormGroup)`
  --thumbSize: 24px;
  --trackSize: 8px;
  --trackBg: ${color('blue100')};
  --progressBg: ${color('primary')};
`;

/**
 * `RangeSilder` styled component.
 */

const RangeSilder = styled.input`
  appearance: none;
  display: block;
  min-height: var(--thumbSize);
  width: 100%;

  &::-webkit-slider-thumb {
    ${thumbStyles}
  }

  &::-moz-range-thumb {
    ${thumbStyles}
  }

  &::-webkit-slider-runnable-track {
    ${progressStyles}
  }

  &::-moz-range-track {
    ${progressStyles}
  }

  &:hover,
  &:focus {
    &::-webkit-slider-thumb {
      box-shadow: 0 0 0 8px var(--trackBg);
    }

    &::-moz-range-thumb {
      box-shadow: 0 0 0 8px var(--trackBg);
    }
  }
`;

/**
 * Export `RangeInput` component.
 */

export function RangeInput(props: Props): JSX.Element {
  const { className, disabled, helpText, id, label, max = 100, min = 0, name, step = 1, ...rest } = props;
  const { error, onChange, value = 0, ...fieldProps } = useField(name);
  const { alreadySubmitted, isSubmitting } = useFormState();
  const hasError = !!error && alreadySubmitted;
  const isDisabled = disabled || isSubmitting;
  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    onChange(Number(value));
  }, [onChange]);

  return (
    <StyledFormGroup
      className={className}
      disabled={isDisabled}
    >
      <RangeSilder
        {...fieldProps}
        {...rest}
        aria-label={label}
        id={id ?? name}
        max={max}
        min={min}
        onChange={handleChange}
        step={step}
        type={'range'}
        value={value}
      />

      {helpText && (
        <FieldAlert isOpen={!hasError}>
          {helpText}
        </FieldAlert>
      )}

      <FieldError
        error={error}
        isOpen={hasError}
        name={name}
      />
    </StyledFormGroup>
  );
}
